import axios from 'axios';
import _ from 'lodash';
import {
  getCookie,
  removeCookie,
  setLocalStorageItem,
  getLocalStorageItem,
  removeLocalStorageItem,
} from 'helpers/Storage';
import { setMVPDCookie as setMVPDClosureValue, setMVPDLogoutCookie } from 'helpers/ContentHelper';
import { getAccountCookie, setAccountCookie, convertTimestamp } from 'helpers/Account';
import { noop } from 'helpers/GlobalHelper';
import { differenceInMilliseconds, msToDate, now } from 'helpers/DateTimeHelper';
import { getBaseContentUrl } from 'helpers/ApiHelper';

export const DEVICE_TYPE = 'web';
const ENDPOINT_BUYTHROUGH_SCENARIOS = 'buythroughscenarios';
export const ENDPOINT_CHECK_AUTHENTICATION = 'checkAuthentication';
export const ENDPOINT_GET_AUTHENTICATION = 'getAuthentication';
export const ENDPOINT_GET_ENTITLEMENTS = 'getEntitlements';
export const ENDPOINT_LOGOUT = 'mvpdLogout';
const ENDPOINT_PREVIEW_PASS = 'previewPass';
const ENDPOINT_UPGRADE_JWT = 'upgradeToken';
const ENDPOINT_USER_METADATA = 'usermetadata';
export const MVPD_SCENARIO_DIRECT = 'mvpdDirect';

export const PREVIEW_PASS_60 = 'TempPass_fbcfox_60min';
export const PREVIEW_PASS_5 = 'TempPass_fbcfox_5min';
export const VCHIP_RATINGS = ['tv-y', 'tv-y7', 'tv-g', 'tv-pg', 'tv-14', 'tv-ma', 'nr'];

export const setLocalStorage = (key, data) => {
  try {
    setLocalStorageItem({ key, value: JSON.stringify(data) });
  } catch (error) {
    console.log('Setting cache error', error);
  }
};
export const getLocalStorage = key => {
  let item = null;
  try {
    item = JSON.parse(getLocalStorageItem({ key }));
  } catch (error) {
    console.log('Getting cache error', error);
  }
  return item;
};
export const removeLocalStorage = key => {
  try {
    removeLocalStorageItem({ key });
  } catch (error) {
    console.log('Removing cache error', error);
  }
};

export const getAccessToken = data => _.get(data, 'accessToken', null);
export const getTokenExpiration = data => _.get(data, 'tokenExpiration', null);
export const getAuthNExpiration = data => _.get(data, 'authn_expire', null);
export const isTimestampExpired = timestamp => {
  const current = new Date();
  const expiration = new Date(timestamp);
  if (!expiration.getTime() || current > expiration) return true;
  return false;
};
export const isTokenExpired = data => isTimestampExpired(getTokenExpiration(data));

export function isPreviewPass(id) {
  return id === PREVIEW_PASS_5 || id === PREVIEW_PASS_60 || false;
}

export const setMVPDCookie = (name, value) => {
  const tokenExpiration = getTokenExpiration(value);
  const expires = convertTimestamp(tokenExpiration);
  const newCookie = {
    name,
    value,
    options: {
      path: '/',
      expires,
    },
  };
  setMVPDClosureValue(newCookie);
};

export const removeMVPDCookie = name => {
  const options = {
    path: '/',
  };
  removeCookie({ key: name, options });
};

export const getApiUrl = options => {
  const { endpoint, authEndpointConfig, mvpdId, placeholder, placeholderValue } = options;
  const baseUrl = getBaseContentUrl();
  let baseUri = _.get(authEndpointConfig, endpoint, endpoint);
  if (placeholder && placeholderValue) baseUri = baseUri.replace(placeholder, placeholderValue);
  const completeApiUrl = baseUrl && baseUri;

  if (!completeApiUrl) return null;

  const urlParams = mvpdId && endpoint === ENDPOINT_BUYTHROUGH_SCENARIOS ? `?mvpdId=${mvpdId}` : '';
  return `${baseUrl}${baseUri}${urlParams}`;
};

export const getApiHeaders = options => {
  const apiKey = _.get(options, 'authEndpointConfig.apiKey', null);
  const accessToken = _.get(options, 'accessToken', null);
  const deviceId = _.get(options, 'deviceId', null);
  const xSignatureEnabled = _.get(options, 'xSignatureEnabled', null);
  const headers = {};

  if (apiKey) headers['X-Api-Key'] = apiKey;
  if (accessToken) headers.Authorization = accessToken;
  if (deviceId) headers['x-dcg-udid'] = deviceId;
  if (xSignatureEnabled) headers['x-signature-enabled'] = xSignatureEnabled;

  return headers;
};

export const sendHttpRequest = ({ url, method, params, headers } = {}) =>
  new Promise(async (resolve, reject) => {
    try {
      const result = await axios({ url, method, params, headers });
      const data = _.get(result, 'data');
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });

export const getPreviewPass = (options = {}) => {
  const { authEndpointConfig, deviceId, accessToken, mvpdId } = options;
  const method = 'GET';
  const endpoint = ENDPOINT_PREVIEW_PASS;
  const url = getApiUrl({ authEndpointConfig, endpoint });
  const headers = getApiHeaders({
    authEndpointConfig,
    endpoint,
    accessToken,
    deviceId,
    xSignatureEnabled: true,
  });
  const params = {
    device_id: deviceId,
    mvpd_id: mvpdId,
  };

  return sendHttpRequest({ url, method, params, headers });
};

export const checkPreviewPass = async (options = {}) => {
  const { mvpdCookieName, setMVPDToAppState } = options;
  const setMVPDState = setMVPDToAppState || noop;

  return new Promise(async (resolve, reject) => {
    try {
      const result = await getPreviewPass(options);
      setMVPDCookie(mvpdCookieName, result);
      setMVPDState(result);
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
};

export const getAuthentication = (options = {}) => {
  const {
    appVersion,
    authEndpointConfig,
    deviceId,
    deviceType,
    firstScreen,
    accessToken,
    model,
    mvpdId,
    osName,
    redirectUrl,
    requestor,
  } = options;
  const method = 'GET';
  const endpoint = ENDPOINT_GET_AUTHENTICATION;
  const url = getApiUrl({ authEndpointConfig, endpoint });
  const headers = getApiHeaders({
    authEndpointConfig,
    endpoint,
    accessToken,
    deviceId,
    xSignatureEnabled: true,
  });
  const params = {
    device_id: deviceId,
    device_type: deviceType,
    app_version: appVersion,
    mvpd_id: mvpdId,
    first_screen: firstScreen,
    redirect_url: redirectUrl,
    requestor,
    model,
    osName,
  };

  return sendHttpRequest({ url, method, params, headers });
};

const checkAuthenticationRequest = (options = {}) => {
  const { deviceId, deviceType, requestor } = options;

  const apiOptions = {
    ...options,
    endpoint: ENDPOINT_CHECK_AUTHENTICATION,
    xSignatureEnabled: true,
  };
  const method = 'GET';
  const url = getApiUrl(apiOptions);
  const headers = getApiHeaders(apiOptions);
  const params = {
    device_id: deviceId,
    device_type: deviceType,
    requestor,
  };
  return sendHttpRequest({ url, method, params, headers });
};

export const checkAuthentication = async (options = {}) => {
  const { mvpdCookieName, setMVPDToAppState, setProvider = noop } = options;
  const setMVPDState = setMVPDToAppState || noop;

  return new Promise(async (resolve, reject) => {
    try {
      const result = await checkAuthenticationRequest(options);
      setMVPDCookie(mvpdCookieName, result);
      setMVPDState(result);
      resolve(result);
    } catch (error) {
      const mvpdCookie = getCookie({ name: mvpdCookieName });
      if (mvpdCookie) {
        const mvpd = _.get(mvpdCookie, 'mvpd', null);
        const tokenTTL = _.get(mvpdCookie, 'authn_expire', null);
        const tokenDiff = differenceInMilliseconds(msToDate(tokenTTL), now());
        // If User was logged in with PreviewPass and token is still valid, don't remove cookie
        if (!(isPreviewPass(mvpd) && tokenDiff > 0)) {
          removeMVPDCookie(mvpdCookieName);
          setProvider(null);
          setMVPDState(null);
        }
      }
      reject(error);
    }
  });
};

export const getEntitlements = async (options = {}) => {
  try {
    const { setMVPDToAppState, mvpdCookieName } = options;
    const apiOptions = {
      ...options,
      endpoint: ENDPOINT_GET_ENTITLEMENTS,
      xSignatureEnabled: true,
    };
    const setMVPDState = setMVPDToAppState || noop;
    const method = 'GET';
    const url = getApiUrl(apiOptions);
    const headers = getApiHeaders(apiOptions);
    const result = await sendHttpRequest({ url, method, headers });
    const mvpdCookie = getCookie({ name: mvpdCookieName });
    const entitlements = _.get(result, 'entitlements');
    const preauthorizedResources = entitlements && _.keys(entitlements);

    if (mvpdCookie) {
      const newMvpdCookie = {
        ...mvpdCookie,
        preauthorizedResources,
      };
      setMVPDCookie(mvpdCookieName, newMvpdCookie);
      setMVPDState(newMvpdCookie);
    }

    return result;
  } catch (e) {
    return console.log('Get entitlements error', e);
  }
};

export const getMetadata = async (options = {}) => {
  const { setMVPDToAppState, mvpdCookieName } = options;
  const setMVPDState = setMVPDToAppState || noop;
  const apiOptions = {
    ...options,
    endpoint: ENDPOINT_USER_METADATA,
    xSignatureEnabled: true,
  };

  const method = 'GET';
  const url = getApiUrl(apiOptions);
  const headers = getApiHeaders(apiOptions);
  const result = await sendHttpRequest({ url, method, headers });
  const mvpdCookie = getCookie({ name: mvpdCookieName });

  if (mvpdCookie) {
    const newMvpdCookie = {
      ...mvpdCookie,
      metadata: result,
    };
    setMVPDCookie(mvpdCookieName, newMvpdCookie);
    setMVPDState(newMvpdCookie);
  }

  return result;
};

export const getBuythroughScenarios = async options => {
  const { mvpdId, setMVPDToAppState } = options;
  const setMVPDState = setMVPDToAppState || noop;
  const apiOptions = {
    ...options,
    endpoint: ENDPOINT_BUYTHROUGH_SCENARIOS,
    mvpdId,
    placeholder: '{packageId}',
  };
  const method = 'GET';
  const url = getApiUrl(apiOptions);
  const headers = getApiHeaders(apiOptions);

  const result = await sendHttpRequest({ url, method, headers });
  const upsellUrl = _.get(result, 'url');

  const { mvpdCookieName } = options;
  const mvpdCookie = getCookie({ name: mvpdCookieName });
  if (mvpdCookie && upsellUrl) {
    const newMvpdCookie = {
      ...mvpdCookie,
      buythroughData: result,
    };
    setMVPDCookie(mvpdCookieName, newMvpdCookie);
    setMVPDState(newMvpdCookie);
  }

  return result;
};

export const checkAuthenticationOnWindowFocus = async (options = {}) =>
  new Promise(async (resolve, reject) => {
    let documentFocusTimeout;
    const listener = async () => {
      if (!document.hasFocus()) return;
      clearInterval(documentFocusTimeout);
      try {
        const authResult = await checkAuthentication(options);
        resolve(authResult);
      } catch (e) {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject(null);
      }
    };
    documentFocusTimeout = setInterval(listener, 1000);
  });

export const logout = async (options = {}) => {
  const { setMVPDLogoutDataToAppState } = options;
  const apiOptions = {
    ...options,
    endpoint: ENDPOINT_LOGOUT,
    xSignatureEnabled: true,
  };
  const method = 'DELETE';
  const url = getApiUrl(apiOptions);
  const headers = getApiHeaders(apiOptions);
  return new Promise(async (resolve, reject) => {
    try {
      const result = await sendHttpRequest({ method, url, headers });
      setMVPDLogoutCookie({ value: result });
      setMVPDLogoutDataToAppState(result);
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
};

export const upgradeJWT = async options => {
  const apiOptions = {
    ...options,
    endpoint: ENDPOINT_UPGRADE_JWT,
  };
  const method = 'GET';
  const url = getApiUrl(apiOptions);
  const headers = getApiHeaders(apiOptions);

  const callback = async (resolve, reject) => {
    try {
      const result = await sendHttpRequest({ method, url, headers });
      const accessToken = _.get(result, 'accessToken');
      const tokenExpiration = _.get(result, 'tokenExpiration');
      const shouldUpdateCookie = accessToken && tokenExpiration;
      if (shouldUpdateCookie) {
        const account = getAccountCookie();
        const newAccountData = { ...account, accessToken, tokenExpiration };
        setAccountCookie(newAccountData);
        return resolve(accessToken);
      }
      return resolve(options.accessToken);
    } catch (error) {
      return reject(error);
    }
  };
  return new Promise(callback);
};
